/**
 * Subscription Required Screen
 * Full-page subscription prompt for protected features
 *
 * Used when entire pages should be gated behind subscription
 * (e.g., Team Management for non-subscribers)
 */

import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import type { SubscriptionAccessReason } from '@/shared/hooks/useSubscriptionGuard';

interface SubscriptionRequiredScreenProps {
  /**
   * Name of the feature being gated
   * @example "Team Management"
   */
  feature: string;

  /**
   * Reason for access denial
   */
  reason: SubscriptionAccessReason;

  /**
   * Callback when user clicks upgrade button
   * Usually navigates to /profile?tab=billing
   */
  onUpgrade: () => void;

  /**
   * Optional custom message to display
   */
  customMessage?: string;
}

export function SubscriptionRequiredScreen({
  feature,
  reason,
  onUpgrade,
  customMessage,
}: SubscriptionRequiredScreenProps) {
  const config = getConfigForReason(reason, feature);

  return (
    <div className="container mx-auto p-6 max-w-2xl flex flex-col items-center justify-center min-h-[60vh]">
      <Card className="p-8 text-center">
        {/* Title */}
        <h2 className="text-2xl font-bold mb-3">{config.title}</h2>

        {/* Description */}
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          {customMessage || config.description}
        </p>

        {/* Features List (for no_subscription state) */}
        {reason === 'no_subscription' && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
            <p className="text-sm font-medium mb-2">What you'll get:</p>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Access to {feature}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Unlimited video calls with customers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Advanced scheduling and recording features</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Priority support and updates</span>
              </li>
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-center">
          <Button
            onClick={onUpgrade}
            size="lg"
            className="px-8"
          >
            {config.buttonText}
          </Button>
        </div>

        {/* Additional Help Text */}
        {(reason === 'paused' || reason === 'canceled') && (
          <p className="text-xs text-muted-foreground mt-4">
            Need help? Contact support for assistance.
          </p>
        )}
      </Card>
    </div>
  );
}

/**
 * Get configuration text based on access reason
 */
function getConfigForReason(reason: SubscriptionAccessReason, feature: string) {
  switch (reason) {
    case 'paused':
      return {
        title: 'Subscription Paused',
        description: `Your subscription is currently paused. Reactivate your subscription to continue using ${feature}.`,
        buttonText: 'Reactivate Subscription',
      };

    case 'canceled':
      return {
        title: 'Subscription Canceled',
        description: `Your subscription has been canceled. Resubscribe to regain access to ${feature}.`,
        buttonText: 'Resubscribe Now',
      };

    case 'expired':
      return {
        title: 'Subscription Expired',
        description: `Your subscription has expired. Renew now to continue using ${feature}.`,
        buttonText: 'Renew Subscription',
      };

    case 'no_subscription':
    default:
      return {
        title: `Subscribe to Access ${feature}`,
        description: `${feature} is a premium feature. Subscribe to unlock this and other advanced features.`,
        buttonText: 'View Plans',
      };
  }
}
